/**
 * Elyon Session Replay — browser-side capture (Operational Session Replay,
 * Layer 1: User events). Framework-agnostic vanilla JS, no dependencies.
 *
 * Captures WHAT a user did, never what they typed: clicks (target
 * tag/id/class/truncated text), navigation, and form submissions (form
 * id/action only — field values are never read or sent). Batches locally
 * and POSTs to a same-origin proxy endpoint — this script never holds or
 * sends any secret, since it ships to every visitor's browser.
 *
 * Usage: import and call once at app startup.
 *   import { initElyonSessionReplay } from "./elyon-session-replay";
 *   initElyonSessionReplay({ proxyUrl: "/api/telemetry/session-events" });
 */

const SESSION_STORAGE_KEY = "elyon_session_id";
const DEFAULT_FLUSH_INTERVAL_MS = 10000;
const DEFAULT_MAX_BATCH_SIZE = 50;
const MAX_TEXT_LENGTH = 60;

function getOrCreateSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    }
    return id;
  } catch {
    // sessionStorage unavailable (private browsing, etc.) — fall back to
    // an in-memory id that just won't survive a page reload.
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function truncate(text) {
  if (!text) return "";
  const trimmed = text.trim().replace(/\s+/g, " ");
  return trimmed.length > MAX_TEXT_LENGTH ? trimmed.slice(0, MAX_TEXT_LENGTH) + "…" : trimmed;
}

function describeTarget(el) {
  if (!el || el === document) return null;
  return {
    tag: el.tagName ? el.tagName.toLowerCase() : null,
    id: el.id || null,
    class_name: typeof el.className === "string" ? el.className : null,
    text: truncate(el.textContent),
  };
}

export function initElyonSessionReplay({
  proxyUrl,
  flushIntervalMs = DEFAULT_FLUSH_INTERVAL_MS,
  maxBatchSize = DEFAULT_MAX_BATCH_SIZE,
} = {}) {
  if (typeof window === "undefined") return; // no-op during SSR

  const sessionId = getOrCreateSessionId();
  let queue = [];

  function push(eventType, payload) {
    queue.push({
      timestamp: new Date().toISOString(),
      event_type: eventType,
      payload,
    });
    if (queue.length >= maxBatchSize) flush();
  }

  function flush() {
    if (queue.length === 0) return;
    const batch = queue;
    queue = [];
    const body = JSON.stringify({ session_id: sessionId, events: batch });

    // sendBeacon survives page unload (e.g. navigating away right after a
    // click); fall back to fetch for browsers/contexts without it.
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(proxyUrl, blob);
      if (ok) return;
    }
    fetch(proxyUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {
      // Best-effort — a dropped replay batch never blocks the user.
    });
  }

  document.addEventListener(
    "click",
    (e) => push("click", { target: describeTarget(e.target) }),
    { capture: true, passive: true }
  );

  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      push("form_submit", {
        form_id: form?.id || null,
        action: form?.action || null,
      });
    },
    { capture: true, passive: true }
  );

  function recordNavigation() {
    push("navigation", { path: window.location.pathname + window.location.search });
  }

  const originalPushState = window.history.pushState.bind(window.history);
  window.history.pushState = (...args) => {
    originalPushState(...args);
    recordNavigation();
  };
  const originalReplaceState = window.history.replaceState.bind(window.history);
  window.history.replaceState = (...args) => {
    originalReplaceState(...args);
    recordNavigation();
  };
  window.addEventListener("popstate", recordNavigation);

  recordNavigation(); // initial page load

  setInterval(flush, flushIntervalMs);
  window.addEventListener("pagehide", flush);
}
